import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Card, InputGroup } from 'react-bootstrap';
import { 
    getProductCategories, 
    getFurnitureTypes, 
    getFurnitureTypesByCategory,
    getFurnitureModelsByTypeId,
    getModelColorsByModelId,
    getModelSizesByModelId,
    logError 
} from '../../api';
import SmartSelect from './SmartSelect';

const ProductEditorForm = ({ formData, onChange, product, mode, onOptionsLoad, onConflictResolution }) => {
    const [options, setOptions] = useState({
        categories: [],
        types: [],
        models: [],
        colors: [],
        sizes: []
    });
    const [loading, setLoading] = useState({
        categories: false,
        types: false,
        models: false,
        colors: false,
        sizes: false
    });


    // Cargar categorías al inicio
    useEffect(() => {
        loadCategories();
    }, []);

    // Cargar tipos cuando cambia la categoría
    useEffect(() => {
        if (formData.category) {
            const category = options.categories.find(c => c.categoryName === formData.category);
            if (category) {
                loadTypes(category.id);
            }
        } else {
            setOptions(prev => ({ ...prev, types: [], models: [], colors: [], sizes: [] }));
        }
    }, [formData.category, options.categories]);

    // Cargar modelos cuando cambia el tipo
    useEffect(() => {
        if (formData.type) {
            const type = options.types.find(t => t.typeName === formData.type);
            if (type) {
                // Limpiar colores y tamaños antes de cargar nuevos modelos
                setOptions(prev => ({ ...prev, models: [], colors: [], sizes: [] }));
                loadModels(type.id);
            }
        } else {
            setOptions(prev => ({ ...prev, models: [], colors: [], sizes: [] }));
        }
    }, [formData.type, options.types]);

    // Limpiar colores y tamaños cuando no hay modelo
    useEffect(() => {
        if (!formData.model) {
            setOptions(prev => ({ ...prev, colors: [], sizes: [] }));
        }
    }, [formData.model]);



    const loadCategories = async () => {
        setLoading(prev => ({ ...prev, categories: true }));
        try {
            const categories = await getProductCategories();
            setOptions(prev => {
                const newOptions = { ...prev, categories };
                onOptionsLoad?.(newOptions);
                return newOptions;
            });
        } catch (error) {
            logError(error);
        } finally {
            setLoading(prev => ({ ...prev, categories: false }));
        }
    };

    const loadTypes = async (categoryId) => {
        setLoading(prev => ({ ...prev, types: true }));
        try {
            const types = await getFurnitureTypesByCategory(categoryId);
            setOptions(prev => {
                const newOptions = { ...prev, types };
                onOptionsLoad?.(newOptions);
                return newOptions;
            });
            
            // Seleccionar automáticamente el primer tipo solo si no hay tipo seleccionado
            if (types.length > 0 && !formData.type) {
                const firstType = types[0];
                handleFieldChange('type', firstType.typeName);
            }
        } catch (error) {
            logError(error);
        } finally {
            setLoading(prev => ({ ...prev, types: false }));
        }
    };

    const loadModels = async (typeId) => {
        setLoading(prev => ({ ...prev, models: true }));
        try {
            const models = await getFurnitureModelsByTypeId(typeId);
            setOptions(prev => {
                const newOptions = { ...prev, models };
                onOptionsLoad?.(newOptions);
                return newOptions;
            });
            
            if (models.length > 0) {
                if (!formData.model) {
                    // Seleccionar automáticamente el primer modelo si no hay modelo seleccionado
                    const firstModel = models[0];
                    const newData = { ...formData, model: firstModel.modelName, color: '', size: '' };
                    onChange(newData);
                    
                    // Cargar colores y tamaños para este modelo
                    loadColors(firstModel.id);
                    loadSizes(firstModel.id);
                } else {
                    // Si ya hay un modelo seleccionado, cargar sus colores y tamaños
                    const selectedModel = models.find(m => m.modelName === formData.model);
                    if (selectedModel) {
                        loadColors(selectedModel.id);
                        loadSizes(selectedModel.id);
                    }
                }
            }
        } catch (error) {
            logError(error);
        } finally {
            setLoading(prev => ({ ...prev, models: false }));
        }
    };

    const loadColors = async (modelId) => {
        setLoading(prev => ({ ...prev, colors: true }));
        try {
            const colors = await getModelColorsByModelId(modelId);
            setOptions(prev => {
                const newOptions = { ...prev, colors };
                onOptionsLoad?.(newOptions);
                return newOptions;
            });
        } catch (error) {
            logError(error);
        } finally {
            setLoading(prev => ({ ...prev, colors: false }));
        }
    };

    const loadSizes = async (modelId) => {
        setLoading(prev => ({ ...prev, sizes: true }));
        try {
            const sizes = await getModelSizesByModelId(modelId);
            setOptions(prev => {
                const newOptions = { ...prev, sizes };
                onOptionsLoad?.(newOptions);
                return newOptions;
            });
        } catch (error) {
            logError(error);
        } finally {
            setLoading(prev => ({ ...prev, sizes: false }));
        }
    };

    const handleFieldChange = (field, value, directId = null) => {
        // Solo cargar colores y tamaños si el modelo existe en la lista actual
        if (field === 'model' && value && options.models.length > 0) {
            const model = options.models.find(m => m.modelName === value);
            if (model) {
                loadColors(model.id);
                loadSizes(model.id);
            }
        }
        
        applyFieldChange(field, value);
    };


    const applyFieldChange = (field, value) => {
        const newData = { ...formData, [field]: value };
        
        // Limpiar campos dependientes cuando cambian los padres
        if (field === 'category') {
            newData.type = '';
            newData.model = '';
            newData.color = '';
            newData.size = '';
        } else if (field === 'type') {
            newData.model = '';
            newData.color = '';
            newData.size = '';
        } else if (field === 'model') {
            newData.color = '';
            newData.size = '';
        }
        
        onChange(newData);
    };



    return (
        <div className="product-editor-form">
            {/* Información del producto */}
            <Card className="mb-4">
                <Card.Header>
                    <h6 className="mb-0">
                        <i className="bi bi-box me-2"></i>
                        Información del Producto
                    </h6>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <SmartSelect
                                label="Categoría"
                                name="category"
                                value={formData.category}
                                onChange={(e) => handleFieldChange('category', e.target.value)}
                                options={options.categories}
                                displayField="categoryName"
                                loading={loading.categories}
                                placeholder="Seleccionar categoría"
                                allowCustom={true}
                                required={true}
                                mode={mode}
                            />
                        </Col>
                        <Col md={6}>
                            <SmartSelect
                                label="Tipo"
                                name="type"
                                value={formData.type}
                                onChange={(e) => handleFieldChange('type', e.target.value)}
                                options={options.types}
                                displayField="typeName"
                                loading={loading.types}
                                placeholder="Seleccionar tipo"
                                allowCustom={true}
                                required={true}
                                disabled={!formData.category}
                                mode={mode}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <SmartSelect
                                label="Modelo"
                                name="model"
                                value={formData.model}
                                onChange={(e) => handleFieldChange('model', e.target.value)}
                                options={options.models}
                                displayField="modelName"
                                loading={loading.models}
                                placeholder="Seleccionar modelo"
                                allowCustom={true}
                                required={true}
                                disabled={!formData.type}
                                mode={mode}
                            />
                        </Col>
                        <Col md={6}>
                            <SmartSelect
                                label="Color"
                                name="color"
                                value={formData.color}
                                onChange={(e) => {
                                    const selectedOption = options.colors.find(c => c.colorName === e.target.value);
                                    handleFieldChange('color', e.target.value, selectedOption?.id);
                                }}
                                options={options.colors}
                                displayField="colorName"
                                loading={loading.colors}
                                placeholder="Seleccionar color"
                                allowCustom={true}
                                disabled={!formData.model}
                                mode={mode}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <SmartSelect
                                label="Tamaño"
                                name="size"
                                value={formData.size}
                                onChange={(e) => {
                                    const selectedOption = options.sizes.find(s => s.size === e.target.value);
                                    handleFieldChange('size', e.target.value, selectedOption?.id);
                                }}
                                options={options.sizes}
                                displayField="size"
                                loading={loading.sizes}
                                placeholder="Seleccionar tamaño"
                                allowCustom={true}
                                disabled={!formData.model}
                                mode={mode}
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Puntuación</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    value={formData.score || ''}
                                    onChange={(e) => handleFieldChange('score', e.target.value)}
                                    placeholder="0"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Precios y comisiones */}
            <Card className="mb-4">
                <Card.Header>
                    <h6 className="mb-0">
                        <i className="bi bi-currency-euro me-2"></i>
                        Precios y Comisiones
                    </h6>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Precio <span className="text-danger">*</span>
                                </Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formData.price}
                                        onChange={(e) => handleFieldChange('price', e.target.value)}
                                        placeholder="0.00"
                                        required
                                    />
                                    <InputGroup.Text>€</InputGroup.Text>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Modificador Comisión (%)</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="number"
                                        step="0.1"
                                        value={formData.commissionPercentageModifier}
                                        onChange={(e) => handleFieldChange('commissionPercentageModifier', e.target.value)}
                                        placeholder="0.0"
                                    />
                                    <InputGroup.Text>%</InputGroup.Text>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Modificador Comisión Fijo</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="number"
                                        step="1"
                                        value={formData.commissionFixedModifier}
                                        onChange={(e) => handleFieldChange('commissionFixedModifier', e.target.value)}
                                        placeholder="0"
                                    />
                                    <InputGroup.Text>€</InputGroup.Text>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>


        </div>
    );
};

export default ProductEditorForm;